"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Scale, BookOpen, Award, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const principles = [{
  icon: ShieldCheck,
  title: "Independent carrier access",
  description: "We compare options across top-rated carriers so you receive recommendations tailored to your goals, not an insurer's sales target."
}, {
  icon: Scale,
  title: "Fiduciary transparency",
  description: "Every fee, limitation, and guarantee is explained upfront in plain language before you make any commitment."
}, {
  icon: BookOpen,
  title: "Educational clarity",
  description: "We focus on helping you understand how annuities, life coverage, and legacy strategies work together for long-term peace of mind."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 14
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Story() {
  return <section id="our-story" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Narrative & Principles */}
          <motion.div className="lg:col-span-7 flex flex-col space-y-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }}>
            {/* Header / Eyebrow */}
            <motion.div variants={itemVariants} className="space-y-3">
              <Badge variant="outline" className="bg-muted text-foreground border-border text-xs uppercase tracking-wider px-3 py-1 font-semibold rounded-full">
                Our story & mission
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-foreground leading-[1.2] font-default tracking-tight">
                Advisory built on education, not sales quotas.
              </h2>

              {/* Architectural anchor dot rule */}
              <div className="flex items-center gap-2 pt-2">
                <div className="size-2 rounded-full bg-chart-2 shrink-0" />
                <div className="h-px bg-border w-24 sm:w-32" />
              </div>
            </motion.div>

            {/* Narrative Body Copy */}
            <motion.div variants={itemVariants} className="space-y-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              <p>
                DJTD Consulting combines licensed insurance experience with a team of human and AI-supported resources to provide education on fixed and fixed indexed annuities, life insurance, income protection, retirement income planning, and legacy planning.
              </p>
              <p>
                Our role is to help you understand available options, important considerations, and questions to ask so you can make informed decisions based on your individual needs and circumstances. When licensed or professional judgment is required, those matters are handled by the appropriately qualified human professional.
              </p>
            </motion.div>

            {/* Advisory Principles Stack */}
            <motion.div variants={containerVariants} className="space-y-4 pt-2">
              {principles.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} data-index={i} variants={itemVariants}>
                    <Card className="border border-border bg-card shadow-xs rounded-xl transition-[border-color,box-shadow] duration-200 hover:border-chart-2/40 hover:shadow-sm">
                      <CardContent className="p-5 sm:p-6 flex items-start gap-4">
                        <div className="size-10 rounded-lg bg-muted flex items-center justify-center text-foreground shrink-0 border border-border/60">
                          <Icon className="size-5" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="font-semibold text-foreground text-base sm:text-lg font-default">
                            {item.title}
                          </h3>
                          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>

            {/* Supportive Action Callout */}
            <motion.div variants={itemVariants} className="pt-2 text-center">
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Guidance built on education, transparency, and independent access.
              </p>
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Frame & Credential Card */}
          <motion.div className="lg:col-span-5 flex flex-col items-center justify-center relative lg:sticky lg:top-28" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }}>
            {/* Main Photography Frame */}
            <div className="relative w-full rounded-2xl overflow-hidden border border-border bg-card shadow-md">
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consulting_team_meeting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consulting_team_meeting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/consulting_team_meeting.png-wvc-sizes" alt="DJTD Consulting licensed professional reviewing financial plans with clients in a warm sunlit office" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-linear-to-t from-primary/70 via-transparent to-transparent opacity-60" />
              </div>

              {/* Floating Credential Plaque */}
              <div className="p-6 bg-card border-t border-border space-y-4">
                <div className="flex items-start gap-3">
                  <div className="size-9 rounded-full bg-muted flex items-center justify-center text-chart-2 shrink-0 border border-border">
                    <Award className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-base">
                      Licensed Insurance Professionals
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      DJTD Consulting Advisory Practice
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/60">
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-chart-2 shrink-0" />
                    <span>Independent access</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-chart-2 shrink-0" />
                    <span>Zero carrier quotas</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-chart-2 shrink-0" />
                    <span>Plain language advice</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-chart-2 shrink-0" />
                    <span>Fiduciary transparency</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}